import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from '../utils/i18n';
import { useAuth } from '../contexts/AuthContext';
import './Header.css';
import HeaderIcon from './icons/headerIcon';
import UserIcon from './icons/UserIcon';
import HamburgerIcon from './icons/HamburgerIcon';
import LocaleSelector from './LocaleSelector';
import CookieBanner from './CookieBanner';
import SuspendedAlertModal from './SuspendedAlertModal';

const Header: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, userRegisterStore, logout, userSuspended } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isNavbarOpen, setIsNavbarOpen] = useState(false);
  const [showSuspendedAlert, setShowSuspendedAlert] = useState(false);
  const dropdownRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Only show navigation links if user is registered
  const links = userRegisterStore ? [
    { path: "/home", title: t("front.navbar.landing") },
    { path: "/request", title: t("front.navbar.request") },
    { path: "/data", title: t("front.navbar.data") },
    {
      path: "/edit",
      title: t("front.navbar.permissionChange")
    }
  ] : [];

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  // Handle navigation with suspension check
  const handleNavigation = (path: string) => {
    // Only show suspension alert for /data path (เริ่มใช้งานข้อมูล)
    if (userSuspended && path === '/data') {
      setShowSuspendedAlert(true);
    } else {
      navigate(path);
    }
  };

  // Close suspended alert
  const closeSuspendedAlert = () => {
    setShowSuspendedAlert(false);
  };

  return (
    <>
      <CookieBanner />
      <nav className="navbar navbar-expand-lg navbar-dark" id="nav2">
        <div className="nev-blur nav-flex-wrap" id="div35">
          <button
            onClick={() => userRegisterStore ? navigate('/home') : navigate('/login')}
            className="navbar-brand"
            style={{ border: 'none', background: 'transparent' }}
          >
            <HeaderIcon />
          </button>

          <button
            type="button"
            id="button4"
            className="navbar-toggler"
            aria-expanded={isNavbarOpen}
            onClick={() => setIsNavbarOpen(!isNavbarOpen)}
            style={{ marginLeft: '20px' }}
          >
            <HamburgerIcon />
          </button>

          <div id="collapsibleNavbar" className={`navbar-collapse nevbar-hide ${isNavbarOpen ? 'show' : 'collapse'}`}>
            <ul id="ulManu" className="navbar-nav ml-auto">
              {links.map((link) => (
                <li key={link.path} className="nav-item">
                  <button
                    className={`nav-link text-nowrap ${location.pathname === link.path ? 'active' : ''}`}
                    onClick={() => {
                      setIsNavbarOpen(false);
                      handleNavigation(link.path);
                    }}
                    style={{ cursor: 'pointer', background: 'transparent', border: 'none', textDecoration: 'none' }}
                  >
                    {link.title}
                  </button>
                </li>
              ))}
              {userRegisterStore ? (
                <li className="nav-item dropdown" style={{ position: 'relative' }} ref={dropdownRef}>
                  <button
                    className="nav-link dropdown-toggle"
                    id="navbarDropdown"
                    role="button"
                    aria-expanded={isDropdownOpen}
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    style={{ border: 'none', background: 'transparent' }}
                  >
                    <UserIcon />
                  </button>
                  {isDropdownOpen && (
                    <ul
                      className="dropdown-menu show"
                      aria-labelledby="navbarDropdown"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        right: 0,
                        zIndex: 1000,
                        display: 'block',
                        minWidth: '10rem',
                        padding: '0.5rem 0',
                        margin: '0.125rem 0 0',
                        fontSize: '1rem',
                        color: '#212529',
                        textAlign: 'left',
                        backgroundColor: '#fff',
                        backgroundClip: 'padding-box',
                        border: '1px solid rgba(0,0,0,.15)',
                        borderRadius: '0.25rem',
                        boxShadow: '0 0.5rem 1rem rgba(0,0,0,.175)'
                      }}
                    >
                      <li>
                        <button
                          className="dropdown-item"
                          onClick={() => {
                            handleLogout();
                            setIsDropdownOpen(false);
                          }}
                          style={{
                            display: 'block',
                            width: '100%',
                            padding: '0.25rem 1rem',
                            clear: 'both',
                            fontWeight: 400,
                            color: '#212529',
                            textAlign: 'inherit',
                            textDecoration: 'none',
                            whiteSpace: 'nowrap',
                            backgroundColor: 'transparent',
                            border: 0,
                            cursor: 'pointer'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#e9ecef';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                          }}
                        >
                          {t("front.navbar.logout")}
                        </button>
                      </li>
                    </ul>
                  )}
                </li>
              ) : (
                <li className="nav-item">
                  <button
                    className="nav-link text-nowrap"
                    onClick={() => navigate('/login')}
                    style={{ border: 'none', background: 'transparent' }}
                  >
                    {t("front.navbar.login")}
                  </button>
                </li>
              )}
              <div id="div45" className="locale-flex">
                <LocaleSelector />
              </div>
            </ul>
          </div>
        </div>
      </nav>
      
      {/* Suspended Alert Modal */}
      <SuspendedAlertModal
        isOpen={showSuspendedAlert}
        onClose={closeSuspendedAlert}
      />
    </>
  );
};

export default Header;
